import React, { useEffect, useState } from "react";
import { Plus, X, Edit2, Ban, CheckCircle2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ROLES, initials } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email:"", name:"", password:"", role:"colaborador", area_id:"" });
  const [hardDelete, setHardDelete] = useState(null); // user object pending hard delete
  const [confirmText, setConfirmText] = useState("");

  const load = async () => {
    const [u, a] = await Promise.all([api.get("/users"), api.get("/areas")]);
    setUsers(u.data); setAreas(a.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({email:"",name:"",password:"",role:"colaborador",area_id:""}); setShowForm(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email:u.email, name:u.name, password:"", role:u.role, area_id:u.area_id||"" }); setShowForm(true); };

  const save = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) { toast.error("Complete los campos"); return; }
    try {
      if (editing) {
        const upd = { name: form.name, role: form.role, area_id: form.area_id||null };
        if (form.password) upd.password = form.password;
        await api.put(`/users/${editing.id}`, upd);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { ...form, area_id: form.area_id||null });
        toast.success("Usuario creado");
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Usuario desactivado" : "Usuario activado"); load(); }
    catch { toast.error("Error"); }
  };

  const openHardDelete = (u) => { setHardDelete(u); setConfirmText(""); };
  const confirmHardDelete = async () => {
    if (confirmText !== "ELIMINAR") { toast.error("Debe escribir ELIMINAR para confirmar"); return; }
    try {
      await api.delete(`/users/${hardDelete.id}?hard=true`);
      toast.success(`${hardDelete.name} eliminado(a) definitivamente`);
      setHardDelete(null); setConfirmText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const areaMap = Object.fromEntries(areas.map(a => [a.id, a.name]));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Administración</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Usuarios</h1>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-crejpp hover:bg-crejpp-dark text-white font-semibold rounded-md px-4 py-2 text-sm" data-testid="new-user-btn"><Plus className="w-4 h-4" /> Nuevo usuario</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 font-semibold">Usuario</th><th className="px-4 py-3 font-semibold">Correo</th><th className="px-4 py-3 font-semibold">Rol</th><th className="px-4 py-3 font-semibold">Área</th><th className="px-4 py-3 font-semibold">Estado</th><th className="px-4 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{initials(u.name)}</div>
                    <span className="font-medium text-slate-900">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-1 rounded">{ROLES[u.role]}</span></td>
                <td className="px-4 py-3 text-slate-600">{areaMap[u.area_id] || "—"}</td>
                <td className="px-4 py-3">
                  {u.active !== false ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-semibold">Activo</span> : <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full font-semibold">Inactivo</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} className="text-slate-500 hover:text-crejpp p-1.5 inline-flex" title="Editar" data-testid={`edit-user-${u.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => toggleActive(u)} className="text-slate-500 hover:text-crejpp p-1.5 inline-flex" title={u.active !== false ? "Desactivar" : "Activar"} data-testid={`toggle-user-${u.id}`}>{u.active !== false ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}</button>
                  {u.id !== me.id && (
                    <button onClick={() => openHardDelete(u)} className="text-slate-400 hover:text-crejpp p-1.5 inline-flex" title="Eliminar definitivamente" data-testid={`hard-delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-heading text-xl font-bold text-slate-900">{editing ? "Editar usuario" : "Nuevo usuario"}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100 rounded-md"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <F label="Nombre completo"><input className="input" value={form.name} onChange={e => setForm({...form, name:e.target.value})} data-testid="user-name" /></F>
              <F label="Correo institucional"><input type="email" disabled={!!editing} className="input" value={form.email} onChange={e => setForm({...form, email:e.target.value})} data-testid="user-email" /></F>
              <F label={editing ? "Nueva contraseña (opcional)" : "Contraseña"}><input type="password" className="input" value={form.password} onChange={e => setForm({...form, password:e.target.value})} data-testid="user-password" /></F>
              <F label="Rol">
                <select className="input" value={form.role} onChange={e => setForm({...form, role:e.target.value})} data-testid="user-role">
                  {Object.entries(ROLES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </F>
              <F label="Área (opcional)">
                <select className="input" value={form.area_id} onChange={e => setForm({...form, area_id:e.target.value})}>
                  <option value="">— Sin área —</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </F>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
              <button onClick={save} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark text-white rounded-md text-sm font-semibold" data-testid="save-user-btn">{editing ? "Guardar" : "Crear"}</button>
            </div>
            <style>{`.input { width:100%; padding:0.5rem 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; background:white; } .input:focus { outline:none; border-color:#EE1C25; box-shadow:0 0 0 3px rgba(238,28,37,0.12); }`}</style>
          </div>
        </div>
      )}

      {hardDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setHardDelete(null)}>
          <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="hard-delete-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-red-50 text-crejpp flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Eliminar definitivamente</h3>
                <p className="text-sm text-slate-500 mt-1">Esta acción <b>no se puede deshacer</b>. Se eliminará permanentemente al usuario <b className="text-slate-900">{hardDelete.name}</b> ({hardDelete.email}).</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-1 mb-4">
              <p><b>¿Qué ocurrirá?</b></p>
              <ul className="list-disc ml-5 space-y-0.5">
                <li>El usuario será eliminado de la base de datos.</li>
                <li>Sus tareas asignadas quedarán sin responsable (para que otro pueda tomarlas).</li>
                <li>Sus notificaciones se eliminarán.</li>
                <li>Sus comentarios y registros de auditoría permanecerán con su nombre.</li>
              </ul>
              <p className="pt-1">Si sólo desea impedir el acceso, use <b>Desactivar</b> en su lugar.</p>
            </div>
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Para confirmar, escriba <span className="text-crejpp">ELIMINAR</span></span>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="ELIMINAR" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-crejpp focus:ring-2 focus:ring-red-500/20" data-testid="hard-delete-confirm-input" autoFocus />
            </label>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setHardDelete(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm" data-testid="hard-delete-cancel">Cancelar</button>
              <button
                onClick={confirmHardDelete}
                disabled={confirmText !== "ELIMINAR"}
                className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5"
                data-testid="hard-delete-confirm-btn"
              >
                <Trash2 className="w-4 h-4" /> Eliminar definitivamente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) { return <label className="block"><span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}</span>{children}</label>; }
